/**
 * FrontManager - Multi-Front Unit Assignment
 *
 * Assigns units to different fronts/objectives so the AI can pursue
 * multiple goals simultaneously:
 * - ATTACK front: Units pushing toward enemy cities
 * - DEFENSE front: Units protecting threatened friendly cities
 * - EXPANSION front: Units capturing neutral cities
 * - RESERVE: Units not assigned to a specific front
 */

import { UnitCategory } from '../../../types';
import {
  GameStateSnapshot,
  UnitSnapshot,
  CitySnapshot,
  FrontType,
  hexDistance,
  distanceToCity,
  canUnitCapture
} from '../V3Types';
import { getUnitsForFaction, getCitiesForFaction } from './GameStateSnapshot';

// Re-export FrontType for backwards compatibility
export { FrontType };

/**
 * A front represents a group of units working toward a specific objective
 */
export interface Front {
  type: FrontType;
  targetCity?: CitySnapshot;      // Target city for attack/expansion
  defendCity?: CitySnapshot;      // City to defend
  assignedUnits: string[];        // Unit IDs assigned to this front
  priority: number;               // Higher = more important
}

/**
 * Front assignment for the turn
 */
export interface FrontAssignment {
  fronts: Front[];
  unitAssignments: Map<string, FrontType>;  // unitId -> front type
}

/**
 * Analyze the battlefield and create fronts
 */
export function createFronts(snapshot: GameStateSnapshot): FrontAssignment {
  const fronts: Front[] = [];
  const unitAssignments = new Map<string, FrontType>();

  const ourUnits = getUnitsForFaction(snapshot, snapshot.currentFaction);
  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);
  const ourCities = getCitiesForFaction(snapshot, snapshot.currentFaction);
  const enemyCities = getCitiesForFaction(snapshot, snapshot.enemyFaction);
  const neutralCities = snapshot.cities.filter(c => c.faction === null);

  // 1. Create DEFENSE fronts for threatened cities (highest priority)
  const threatenedCities = findThreatenedCities(ourCities, enemyUnits);
  for (const city of threatenedCities) {
    fronts.push({
      type: FrontType.DEFENSE,
      defendCity: city,
      assignedUnits: [],
      priority: 100  // Highest priority
    });
  }

  // 1.5. Create GARRISON fronts for cities that need units to stay (frontline, near enemies)
  // Skip cities already covered by DEFENSE fronts
  const garrisonNeeds = findCitiesNeedingGarrison(ourCities, enemyUnits, ourUnits, enemyCities);
  for (const { city, urgency } of garrisonNeeds) {
    // Skip if already has a DEFENSE front
    const hasDefenseFront = fronts.some(f =>
      f.type === FrontType.DEFENSE && f.defendCity?.name === city.name
    );
    if (!hasDefenseFront) {
      // Priority formula: base 70 + urgency scaling
      // With urgency 120 (upgrading): 70 + 48 = 118 (beats EXPANSION's max of ~105)
      // With urgency 150 (infantry nearby): 70 + 60 = 130 (very high)
      const garrisonPriority = Math.min(98, 70 + Math.floor(urgency * 0.4));  // Just below DEFENSE(100)
      fronts.push({
        type: FrontType.GARRISON,
        defendCity: city,
        assignedUnits: [],
        priority: garrisonPriority
      });
      console.log(`[FrontManager] GARRISON front for ${city.name}: priority=${garrisonPriority} (urgency=${urgency})`);
    }
  }

  // 2. Create EXPANSION fronts for neutral cities
  // KEY: Each neutral city should get exactly 1 unit assigned initially
  // to spread out and capture multiple cities simultaneously
  const expansionCandidates: { city: CitySnapshot; minDistance: number }[] = [];

  for (const city of neutralCities) {
    // Check if any capture-capable unit can reach it (all ground units can capture, aircraft cannot)
    const reachableCapturers = ourUnits.filter(u =>
      canUnitCapture(u) &&
      distanceToCity(u.position, city) <= 20
    );
    if (reachableCapturers.length > 0) {
      // Find the minimum distance from any capturer to this city
      let minDist = Infinity;
      for (const unit of reachableCapturers) {
        const dist = distanceToCity(unit.position, city);
        if (dist < minDist) minDist = dist;
      }
      expansionCandidates.push({ city, minDistance: minDist });
    }
  }

  // Sort by distance (closest first) and assign priorities
  expansionCandidates.sort((a, b) => a.minDistance - b.minDistance);

  for (let i = 0; i < expansionCandidates.length; i++) {
    const { city, minDistance } = expansionCandidates[i];
    // Base priority 70, +20 for being closest, -5 per rank after that
    // Also add distance bonus: closer cities get higher priority
    const rankBonus = Math.max(0, 20 - i * 5);  // 20, 15, 10, 5, 0...
    const distanceBonus = Math.max(0, 15 - minDistance);  // Closer = higher bonus
    const priority = 70 + rankBonus + distanceBonus;

    fronts.push({
      type: FrontType.EXPANSION,
      targetCity: city,
      assignedUnits: [],
      priority
    });
    console.log(`[FrontManager] EXPANSION front for ${city.name}: priority=${priority} (minDist=${minDistance}, rank=${i})`);
  }

  // 3. Create ATTACK fronts for enemy cities
  // Apply FORCE CONCENTRATION check: only launch attacks when units are grouped up
  for (const city of enemyCities) {
    const reachableBy = ourUnits.filter(u => distanceToCity(u.position, city) <= 20);
    if (reachableBy.length === 0) continue;

    // Force concentration check: count units within 8 tiles of target
    const unitsConcentrated = ourUnits.filter(u => distanceToCity(u.position, city) <= 8);
    const unitsClose = ourUnits.filter(u => distanceToCity(u.position, city) <= 4);

    // Estimate enemy defense
    const defense = estimateCityDefense(city, enemyUnits);

    // Calculate attack viability based on concentration
    let priority = 50;  // Base priority

    // BOOST priority if we have good concentration
    if (unitsConcentrated.length >= defense.defenders + 2) {
      // Strong concentrated force - viable attack
      priority += 20;
      console.log(`[FrontManager] ATTACK ${city.name}: STRONG concentration (${unitsConcentrated.length} vs ${defense.defenders} defenders)`);
    } else if (unitsConcentrated.length >= defense.defenders) {
      // Matched force - cautious attack
      priority += 5;
      console.log(`[FrontManager] ATTACK ${city.name}: MATCHED concentration (${unitsConcentrated.length} vs ${defense.defenders} defenders)`);
    } else if (unitsClose.length > 0) {
      // Some units close but not enough - group up first
      priority -= 15;
      console.log(`[FrontManager] ATTACK ${city.name}: WEAK concentration (${unitsConcentrated.length} vs ${defense.defenders}), should group up`);
    } else {
      // Units too scattered - low priority attack
      priority -= 25;
      console.log(`[FrontManager] ATTACK ${city.name}: SCATTERED units (closest: ${reachableBy.length} within 20 tiles)`);
    }

    // Bonus if enemy city is undefended or weakly defended
    if (defense.defenders === 0) {
      priority += 15;  // Easy target
    }

    fronts.push({
      type: FrontType.ATTACK,
      targetCity: city,
      assignedUnits: [],
      priority: Math.max(20, priority)  // Min priority 20
    });
  }

  // 4. Sort fronts by priority
  fronts.sort((a, b) => b.priority - a.priority);

  // 5. ASSIGN UNITS IN PRIORITY ORDER
  // CRITICAL: DEFENSE and GARRISON must be assigned BEFORE EXPANSION
  // Otherwise units on cities that need defending get stolen for expansion
  const unassignedUnits = new Set(ourUnits.map(u => u.id));

  // Separate fronts by type for ordered assignment
  const defenseFronts = fronts.filter(f => f.type === FrontType.DEFENSE);
  const garrisonFronts = fronts.filter(f => f.type === FrontType.GARRISON);
  const expansionFronts = fronts.filter(f => f.type === FrontType.EXPANSION);
  const attackFronts = fronts.filter(f => f.type === FrontType.ATTACK);

  // 5a. DEFENSE fronts first (highest priority - under active threat)
  for (const front of defenseFronts) {
    assignUnitsToFront(front, ourUnits, unassignedUnits, snapshot);
  }

  // 5b. GARRISON fronts second (must keep cities defended!)
  for (const front of garrisonFronts) {
    assignUnitsToFront(front, ourUnits, unassignedUnits, snapshot);
  }

  // 5c. EXPANSION fronts third - assign exactly 1 unit to each
  // Only use units NOT assigned to defense/garrison
  for (const front of expansionFronts) {
    assignOneUnitToExpansion(front, ourUnits, unassignedUnits);
  }

  // Second pass: If we have more capturers than neutral cities, add extras
  const remainingCapturers = ourUnits.filter(u =>
    unassignedUnits.has(u.id) &&
    canUnitCapture(u)  // All ground units can capture, aircraft cannot
  );
  if (remainingCapturers.length > 0 && expansionFronts.length > 0) {
    // Distribute extras to expansion fronts that don't have 2 yet
    for (const front of expansionFronts) {
      if (front.assignedUnits.length >= 2) continue;  // Max 2 per neutral city

      const closest = remainingCapturers
        .filter(u => unassignedUnits.has(u.id))
        .sort((a, b) =>
          distanceToCity(a.position, front.targetCity!) - distanceToCity(b.position, front.targetCity!)
        )[0];

      if (closest) {
        front.assignedUnits.push(closest.id);
        unassignedUnits.delete(closest.id);
      }
    }
  }

  // 5d. ATTACK fronts last
  for (const front of attackFronts) {
    assignUnitsToFront(front, ourUnits, unassignedUnits, snapshot);
  }

  // 6. ARMOR MUST FIGHT: Any unassigned armor/artillery goes to nearest ATTACK or DEFENSE front
  // Armor should NEVER be idle in reserve!
  const combatFronts = [...attackFronts, ...defenseFronts].filter(f => f.assignedUnits.length > 0 || f.targetCity || f.defendCity);
  const unassignedArmor = ourUnits.filter(u =>
    unassignedUnits.has(u.id) &&
    (u.category === UnitCategory.ARMOR || u.category === UnitCategory.ARTILLERY)
  );

  for (const armor of unassignedArmor) {
    // Find nearest combat front
    let nearestFront: Front | null = null;
    let nearestDist = Infinity;

    for (const front of combatFronts) {
      const targetPos = front.targetCity?.centerPosition || front.defendCity?.centerPosition;
      if (targetPos) {
        const dist = hexDistance(armor.position, targetPos);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestFront = front;
        }
      }
    }

    // If no combat fronts, find any attack front (even empty ones)
    if (!nearestFront && attackFronts.length > 0) {
      nearestFront = attackFronts[0];
    }

    if (nearestFront) {
      nearestFront.assignedUnits.push(armor.id);
      unassignedUnits.delete(armor.id);
      console.log(`[FrontManager] ARMOR REASSIGN: ${armor.type} -> ${nearestFront.type} ${nearestFront.targetCity?.name || nearestFront.defendCity?.name}`);
    }
  }

  // 7. Remaining units (infantry/recon only now) go to RESERVE
  const reserveFront: Front = {
    type: FrontType.RESERVE,
    assignedUnits: Array.from(unassignedUnits),
    priority: 0
  };
  fronts.push(reserveFront);

  // Build unit assignments map
  for (const front of fronts) {
    for (const unitId of front.assignedUnits) {
      unitAssignments.set(unitId, front.type);
    }
  }

  return { fronts, unitAssignments };
}

/**
 * Assign exactly ONE unit to an expansion front
 * Prioritizes: 1) Units already close/committed, 2) Recon (fast), 3) Other ground units
 * Note: Aircraft cannot capture cities. Infantry is slow so no bonus for expansion.
 */
function assignOneUnitToExpansion(
  front: Front,
  allUnits: UnitSnapshot[],
  unassignedUnits: Set<string>
): void {
  if (!front.targetCity) return;

  // Get capture-capable units sorted by suitability for this city (aircraft cannot capture)
  const candidates = allUnits
    .filter(u =>
      unassignedUnits.has(u.id) &&
      canUnitCapture(u)
    )
    .map(u => {
      const dist = distanceToCity(u.position, front.targetCity!);
      // Score: lower is better
      // Prioritize: 1) Already very close (committed), 2) Recon (fast), 3) Other units
      // Note: Infantry is slow, so no special bonus - only recon gets speed bonus
      const isRecon = u.category === UnitCategory.RECONNAISSANCE;
      const isVeryClose = dist <= 5;  // Already committed to this city
      const isClose = dist <= 10;

      // Committed units get huge priority (don't redirect them!)
      // Only recon gets bonus (they're fast)
      let score = dist;
      if (isVeryClose) score -= 100;  // Heavily favor already-committed units
      else if (isClose) score -= 20;
      if (isRecon) score -= 15;  // Recon bonus (they move faster)

      return { unit: u, distance: dist, score };
    })
    .sort((a, b) => a.score - b.score);

  // Assign the best candidate
  if (candidates.length > 0) {
    const best = candidates[0];
    front.assignedUnits.push(best.unit.id);
    unassignedUnits.delete(best.unit.id);
    console.log(`[FrontManager] Assigned ${best.unit.type} to ${front.targetCity!.name} (dist=${best.distance}, score=${best.score.toFixed(1)})`);
  }
}

/**
 * Find cities that are threatened by enemy units
 */
function findThreatenedCities(
  ourCities: CitySnapshot[],
  enemyUnits: UnitSnapshot[]
): CitySnapshot[] {
  return ourCities.filter(city => {
    // Being captured
    if (city.isBeingCaptured && city.capturingFaction !== null) {
      return true;
    }

    // Enemy units nearby that could capture (ALL units can capture!)
    const enemiesNearCity = enemyUnits.filter(u =>
      distanceToCity(u.position, city) <= 4
    );
    if (enemiesNearCity.length > 0) {
      return true;
    }

    // Enemy units ON the city - CRITICAL THREAT! (ANY unit on city can capture)
    const enemiesOnCity = enemyUnits.filter(u =>
      city.tiles.some(t => t.x === u.position.x && t.y === u.position.y)
    );
    if (enemiesOnCity.length > 0) {
      console.log(`[FrontManager] CRITICAL: Enemy unit ON ${city.name}!`);
      return true;
    }

    // Large enemy force nearby
    const enemiesNearby = enemyUnits.filter(u => distanceToCity(u.position, city) <= 6);
    if (enemiesNearby.length >= 3) {
      return true;
    }

    return false;
  });
}

/**
 * Find cities that need garrisoning (frontline, near enemies, or few defenders)
 * These cities need units to stay ON them to prevent capture
 */
function findCitiesNeedingGarrison(
  ourCities: CitySnapshot[],
  enemyUnits: UnitSnapshot[],
  ourUnits: UnitSnapshot[],
  enemyCities: CitySnapshot[]
): { city: CitySnapshot; urgency: number; minGarrison: number }[] {
  const garrisonNeeds: { city: CitySnapshot; urgency: number; minGarrison: number }[] = [];

  for (const city of ourCities) {
    let urgency = 0;
    let minGarrison = 1;

    // Count defenders currently ON the city (can block capture)
    const defendersOnCity = ourUnits.filter(u =>
      city.tiles.some(t => t.x === u.position.x && t.y === u.position.y)
    ).length;

    // Count enemy threats within 6 tiles
    const nearbyEnemies = enemyUnits.filter(u => distanceToCity(u.position, city) <= 6);
    // Enemies close to city (ALL units can capture!)
    const enemiesCloseToCity = enemyUnits.filter(u =>
      distanceToCity(u.position, city) <= 4
    );
    // Enemies ON the city - CRITICAL! (ANY unit can capture)
    const enemiesOnCity = enemyUnits.filter(u =>
      city.tiles.some(t => t.x === u.position.x && t.y === u.position.y)
    );

    // CRITICAL: Upgrading cities can't produce defenders - MUST keep units on them!
    if (city.isUpgrading) {
      urgency = Math.max(urgency, 120);  // Very high base urgency for upgrading cities
      minGarrison = Math.max(minGarrison, 1);
      console.log(`[FrontManager] City ${city.name} is upgrading - HIGH garrison urgency`);
    }

    // HIGHEST urgency if enemy is ON the city! (ANY unit can capture)
    if (enemiesOnCity.length > 0) {
      urgency = Math.max(urgency, 200);  // MAXIMUM urgency - city is being captured!
      minGarrison = Math.max(2, enemiesOnCity.length + 1);
      console.log(`[FrontManager] CRITICAL: Enemy ON ${city.name}! urgency=200`);
    }
    // High urgency if enemies are close (can capture!)
    else if (enemiesCloseToCity.length > 0) {
      urgency = Math.max(urgency, 150);  // Even higher than upgrading alone
      minGarrison = Math.max(2, enemiesCloseToCity.length);
    } else if (nearbyEnemies.length > 0) {
      urgency = Math.max(urgency, 80 + nearbyEnemies.length * 10);
      minGarrison = Math.max(minGarrison, 2);
    }

    // Check if city is on the frontline (near enemy cities)
    const closestEnemyDist = enemyCities.length > 0 ?
      Math.min(...enemyCities.map(ec => hexDistance(city.centerPosition, ec.centerPosition))) :
      Infinity;

    if (closestEnemyDist <= 10) {
      urgency = Math.max(urgency, 90);
      minGarrison = Math.max(minGarrison, 2);
    }

    // If city has NO defenders ON it, extra urgent!
    if (defendersOnCity === 0 && urgency > 0) {
      urgency += 50;  // Increased from 30 - undefended cities are very vulnerable
    }

    // Only add if there's some urgency
    if (urgency > 0) {
      garrisonNeeds.push({ city, urgency, minGarrison });
    }
  }

  return garrisonNeeds.sort((a, b) => b.urgency - a.urgency);
}

/**
 * Estimate enemy strength defending a city
 */
function estimateCityDefense(
  city: CitySnapshot,
  enemyUnits: UnitSnapshot[]
): { defenders: number; hasArmor: boolean; strength: number } {
  const nearbyEnemies = enemyUnits.filter(u => distanceToCity(u.position, city) <= 5);
  const hasArmor = nearbyEnemies.some(u => u.category === UnitCategory.ARMOR);

  let strength = 0;
  for (const enemy of nearbyEnemies) {
    strength += enemy.category === UnitCategory.ARMOR ? 3 :
                enemy.category === UnitCategory.ARTILLERY ? 2 : 1;
  }

  return { defenders: nearbyEnemies.length, hasArmor, strength };
}

/**
 * Assign units to a front based on proximity, suitability, and required force composition
 */
function assignUnitsToFront(
  front: Front,
  allUnits: UnitSnapshot[],
  unassignedUnits: Set<string>,
  snapshot: GameStateSnapshot
): void {
  const targetPos = front.targetCity?.centerPosition || front.defendCity?.centerPosition;
  if (!targetPos && front.type !== FrontType.RESERVE) return;

  const enemyUnits = getUnitsForFaction(snapshot, snapshot.enemyFaction);

  // Get unassigned units sorted by distance to target
  const availableUnits = allUnits
    .filter(u => unassignedUnits.has(u.id))
    .map(u => ({
      unit: u,
      distance: targetPos ? hexDistance(u.position, targetPos) : Infinity
    }))
    .sort((a, b) => a.distance - b.distance);

  // Count available by category
  const availableInfantry = availableUnits.filter(u => u.unit.category === UnitCategory.INFANTRY);
  const availableArmor = availableUnits.filter(u => u.unit.category === UnitCategory.ARMOR);
  const availableArtillery = availableUnits.filter(u => u.unit.category === UnitCategory.ARTILLERY);

  switch (front.type) {
    case FrontType.DEFENSE: {
      // Assign closest units to defense, need good mix
      let assigned = 0;
      const maxDefenders = Math.min(3, Math.ceil(availableUnits.length * 0.3));

      for (const { unit } of availableUnits) {
        if (assigned >= maxDefenders) break;
        if (!unassignedUnits.has(unit.id)) continue;

        const distToCity = distanceToCity(unit.position, front.defendCity!);
        if (distToCity <= 5 || assigned < 1) {
          front.assignedUnits.push(unit.id);
          unassignedUnits.delete(unit.id);
          assigned++;
        }
      }
      break;
    }

    case FrontType.EXPANSION: {
      // Expansion is handled separately by assignOneUnitToExpansion
      // This case should not be reached, but just in case:
      break;
    }

    case FrontType.ATTACK: {
      // Enemy city - need to assess defense and assign appropriate force
      const defense = estimateCityDefense(front.targetCity!, enemyUnits);

      // Calculate required force: need to outnumber defenders significantly
      // Always include armor for attack power
      let neededInfantry = Math.max(3, defense.defenders + 2);
      let neededArmor = Math.max(1, defense.hasArmor ? Math.ceil(defense.strength / 2) : 1);  // Always at least 1 armor
      let neededArtillery = defense.defenders >= 2 ? 1 : 0;

      // Be more aggressive with unit assignments - use most of available force
      const maxUnitsForFront = Math.ceil(availableUnits.length * 0.8);  // Increased from 0.6
      neededInfantry = Math.min(neededInfantry, Math.ceil(maxUnitsForFront * 0.6), availableInfantry.length);  // Increased from 0.5
      neededArmor = Math.min(neededArmor, Math.ceil(maxUnitsForFront * 0.4), availableArmor.length);  // Increased from 0.3
      neededArtillery = Math.min(neededArtillery, 2, availableArtillery.length);  // Allow 2 artillery

      // FORCE CONCENTRATION: Score units by how many allies they have nearby
      // Prefer assigning grouped units, but don't exclude distant units entirely
      const scoredUnits = availableUnits
        .filter(u => unassignedUnits.has(u.unit.id))
        .map(u => {
          // Count allies within 5 tiles of this unit (increased range)
          const nearbyAllies = allUnits.filter(ally =>
            ally.id !== u.unit.id &&
            ally.faction === snapshot.currentFaction &&
            hexDistance(u.unit.position, ally.position) <= 5
          ).length;

          // Distance to target
          const distToTarget = distanceToCity(u.unit.position, front.targetCity!);

          // Score: prefer grouped units that are reasonably close
          // High nearbyAllies = good (grouped), low distance = good (close)
          // Reduced distance penalty so far units still get assigned
          let score = nearbyAllies * 15 - distToTarget * 0.5;

          // Smaller penalty for lone wolves - they can still join the attack
          if (nearbyAllies === 0 && distToTarget > 10) {
            score -= 20;  // Reduced from -50
          }

          // Bonus for units already close to target (committed)
          if (distToTarget <= 4) {
            score += 30;
          } else if (distToTarget <= 8) {
            score += 15;
          }

          return { ...u, nearbyAllies, score };
        })
        .sort((a, b) => b.score - a.score);  // Higher score = better candidate

      // Split scored units by category
      const scoredArmor = scoredUnits.filter(u => u.unit.category === UnitCategory.ARMOR);
      const scoredInfantry = scoredUnits.filter(u => u.unit.category === UnitCategory.INFANTRY);
      const scoredArtillery = scoredUnits.filter(u => u.unit.category === UnitCategory.ARTILLERY);

      let armorAssigned = 0;
      let infantryAssigned = 0;
      let artilleryAssigned = 0;

      // Assign armor first (prefer grouped armor, but don't skip any)
      for (const { unit } of scoredArmor) {
        if (armorAssigned >= neededArmor) break;
        if (!unassignedUnits.has(unit.id)) continue;

        front.assignedUnits.push(unit.id);
        unassignedUnits.delete(unit.id);
        armorAssigned++;
      }

      // Assign infantry (prefer grouped infantry, but don't skip any)
      for (const { unit } of scoredInfantry) {
        if (infantryAssigned >= neededInfantry) break;
        if (!unassignedUnits.has(unit.id)) continue;

        front.assignedUnits.push(unit.id);
        unassignedUnits.delete(unit.id);
        infantryAssigned++;
      }

      // Assign artillery for support
      for (const { unit } of scoredArtillery) {
        if (artilleryAssigned >= neededArtillery) break;
        if (!unassignedUnits.has(unit.id)) continue;

        front.assignedUnits.push(unit.id);
        unassignedUnits.delete(unit.id);
        artilleryAssigned++;
      }

      console.log(`[FrontManager] ATTACK ${front.targetCity!.name}: assigned ${armorAssigned} armor, ${infantryAssigned} infantry, ${artilleryAssigned} artillery`);
      break;
    }

    case FrontType.GARRISON: {
      // GARRISON: Assign units to stay ON the city to prevent capture
      // Artillery is EXCELLENT for defense (long range, anti-armor)
      // Armor should attack, not garrison
      let assigned = 0;
      let artilleryAssigned = 0;
      const minGarrison = 2;  // Want at least 2 units on garrison
      const wantArtillery = 1;  // Want at least 1 artillery per city for defense

      // First, assign artillery already ON or NEAR the city (artillery is great for defense!)
      const nearbyArtillery = availableUnits.filter(u =>
        unassignedUnits.has(u.unit.id) &&
        u.unit.category === UnitCategory.ARTILLERY &&
        u.distance <= 3
      );
      for (const { unit } of nearbyArtillery) {
        if (artilleryAssigned >= wantArtillery) break;
        if (!unassignedUnits.has(unit.id)) continue;

        front.assignedUnits.push(unit.id);
        unassignedUnits.delete(unit.id);
        assigned++;
        artilleryAssigned++;
        console.log(`[FrontManager] Assigned ${unit.type} to GARRISON ${front.defendCity!.name} (artillery for defense)`);
      }

      // Then assign infantry/recon already ON the city (NOT armor - they should attack)
      for (const { unit } of availableUnits) {
        if (assigned >= minGarrison) break;
        if (!unassignedUnits.has(unit.id)) continue;
        // Skip armor - they should attack, not garrison
        if (unit.category === UnitCategory.ARMOR) continue;
        // Skip artillery - already handled above
        if (unit.category === UnitCategory.ARTILLERY) continue;

        const isOnCity = front.defendCity!.tiles.some(t =>
          t.x === unit.position.x && t.y === unit.position.y
        );
        if (isOnCity) {
          front.assignedUnits.push(unit.id);
          unassignedUnits.delete(unit.id);
          assigned++;
          console.log(`[FrontManager] Assigned ${unit.type} to GARRISON ${front.defendCity!.name} (already on city)`);
        }
      }

      // Then assign closest ground units (they can hold city - aircraft cannot)
      const closeCapturers = availableUnits.filter(u =>
        unassignedUnits.has(u.unit.id) &&
        canUnitCapture(u.unit) &&
        u.unit.category !== UnitCategory.ARMOR &&  // Keep armor for attacking
        u.distance <= 5
      );

      for (const { unit } of closeCapturers) {
        if (assigned >= minGarrison) break;
        if (!unassignedUnits.has(unit.id)) continue;

        front.assignedUnits.push(unit.id);
        unassignedUnits.delete(unit.id);
        assigned++;
        console.log(`[FrontManager] Assigned ${unit.type} to GARRISON ${front.defendCity!.name} (dist=${distanceToCity(unit.position, front.defendCity!)})`);
      }
      break;
    }
  }
}

/**
 * Get the objective position for a unit based on its front assignment
 */
export function getUnitObjective(
  unitId: string,
  assignment: FrontAssignment,
  snapshot: GameStateSnapshot
): { position: { x: number; y: number }; type: FrontType } | null {
  const frontType = assignment.unitAssignments.get(unitId);
  if (!frontType) return null;

  const front = assignment.fronts.find(f =>
    f.type === frontType && f.assignedUnits.includes(unitId)
  );
  if (!front) return null;

  switch (front.type) {
    case FrontType.DEFENSE:
    case FrontType.GARRISON:
      // Both DEFENSE and GARRISON units should move toward/stay on the defended city
      if (front.defendCity) {
        return { position: front.defendCity.centerPosition, type: front.type };
      }
      break;

    case FrontType.EXPANSION:
    case FrontType.ATTACK:
      if (front.targetCity) {
        return { position: front.targetCity.centerPosition, type: front.type };
      }
      break;

    case FrontType.RESERVE:
      // Reserve units move toward the nearest active front
      const unit = snapshot.units.find(u => u.id === unitId);
      if (unit) {
        const activeFronts = assignment.fronts.filter(f =>
          f.type !== FrontType.RESERVE &&
          (f.targetCity || f.defendCity)
        );
        if (activeFronts.length > 0) {
          let nearestFront = activeFronts[0];
          let nearestDist = Infinity;
          for (const f of activeFronts) {
            const pos = f.targetCity?.centerPosition || f.defendCity?.centerPosition;
            if (pos) {
              const dist = hexDistance(unit.position, pos);
              if (dist < nearestDist) {
                nearestDist = dist;
                nearestFront = f;
              }
            }
          }
          const pos = nearestFront.targetCity?.centerPosition || nearestFront.defendCity?.centerPosition;
          if (pos) {
            // IMPORTANT: Return RESERVE type, not the nearest front's type!
            // RESERVE units should move toward active fronts but NOT be constrained like GARRISON units
            return { position: pos, type: FrontType.RESERVE };
          }
        }
      }
      break;
  }

  return null;
}

/**
 * Check if a unit should stay and defend its current position
 */
export function shouldDefendPosition(
  unit: UnitSnapshot,
  assignment: FrontAssignment,
  _snapshot: GameStateSnapshot
): boolean {
  const frontType = assignment.unitAssignments.get(unit.id);

  // Only defend if assigned to defense front
  if (frontType !== FrontType.DEFENSE) return false;

  const front = assignment.fronts.find(f =>
    f.type === FrontType.DEFENSE && f.assignedUnits.includes(unit.id)
  );
  if (!front || !front.defendCity) return false;

  // Check if unit is on or near the city to defend
  const distToCity = distanceToCity(unit.position, front.defendCity);

  // Stay if on city or very close
  return distToCity <= 2;
}

/**
 * Get summary of front assignments for logging
 */
export function getFrontsSummary(assignment: FrontAssignment): string {
  const summary: string[] = [];

  for (const front of assignment.fronts) {
    if (front.assignedUnits.length === 0) continue;

    let target = '';
    if (front.targetCity) target = front.targetCity.name;
    if (front.defendCity) target = front.defendCity.name;

    summary.push(`${front.type}${target ? `(${target})` : ''}: ${front.assignedUnits.length} units`);
  }

  return summary.join(', ');
}
